import {
  faFont,
  faListOl,
  faListUl,
  faQuoteRight,
  IconDefinition,
  faLink, // Added faLink
  faUnlink, // Added faUnlink for removing links
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  convertFromRaw,
  convertToRaw,
  DefaultDraftBlockRenderMap,
  DraftDecoratorType, // Import DraftDecoratorType
  DraftEditorCommand,
  Editor,
  EditorState,
  getDefaultKeyBinding,
  getVisibleSelectionRect,
  Modifier,
  RawDraftContentState,
  RichUtils,
  CompositeDecorator, // Import CompositeDecorator
} from "draft-js";
import { Map as ImmutableMap } from "immutable";
import { observer } from "mobx-react-lite";
import { nanoid } from "nanoid";
import React, {
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useDrag } from "react-dnd";
import styled from "styled-components";
import { useStateWithDeps } from "use-state-with-deps";
import { GENERIC_COMPONENT } from "../Component";
import { ComponentItemProps, DefaultItem } from "../ComponentTree";
import { DragSpec, IEditableComponent } from "../EditableComponent";
import { NewItem } from "../NewComponents";
import { PageContext } from "../../PageContext";
import {
  EditorStyle,
  RichTextComponentSpec,
  RICH_TEXT_COMPONENT,
} from "./RichText";
import "./RichTextExt.css";

// Styled components for the Link Edit Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; // Ensure it's above other elements
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  min-width: 350px;
  color: #333; // Text color for modal content
`;

const ModalInput = styled.input`
  width: calc(100% - 22px); // Adjusted for padding/border
  padding: 8px 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
`;

const ModalButton = styled.button`
  padding: 10px 15px;
  margin-right: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  background-color: #007bff;
  color: white;
  &:hover {
    background-color: #0056b3;
  }
  &.delete {
    background-color: #dc3545;
    &:hover {
      background-color: #c82333;
    }
  }
  &.cancel {
    background-color: #6c757d;
    margin-right: 0; // No margin for the last button
    &:hover {
      background-color: #545b62;
    }
  }
`;

const SelectedTextDisplay = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-style: italic;
  color: #555;
  font-size: 14px;
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
`;

const ModalLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
  color: #333;
`;

// Link Edit Modal Component
interface LinkEditModalProps {
  isOpen: boolean;
  selectedText: string;
  currentUrl: string;
  onSave: (newUrl: string) => void;
  onDelete: () => void;
  onCancel: () => void;
}

const LinkEditModal: React.FC<LinkEditModalProps> = ({
  isOpen,
  selectedText,
  currentUrl,
  onSave,
  onDelete,
  onCancel,
}) => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    // Only update URL from props if the modal is being opened or currentUrl changes
    // This prevents typing from being overridden if parent re-renders for other reasons
    if (isOpen) {
      setUrl(currentUrl);
    }
  }, [currentUrl, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(url);
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
  };

  return (
    <ModalOverlay onClick={onCancel}>
      {" "}
      {/* Optional: close on overlay click */}
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {" "}
        {/* Prevent click through */}
        <ModalTitle>Edit Link</ModalTitle>
        {selectedText && (
          <>
            <ModalLabel htmlFor="link-url">Display Text:</ModalLabel>
            <SelectedTextDisplay>{selectedText}</SelectedTextDisplay>
          </>
        )}
        <ModalLabel htmlFor="link-url">Hyperlink:</ModalLabel>
        <ModalInput
          id="link-url"
          type="text"
          value={url}
          onChange={handleUrlChange}
          placeholder="https://example.com"
          autoFocus
        />
        <div>
          <ModalButton onClick={handleSave}>Save</ModalButton>
          {/* Show delete button only if there is an existing URL (i.e., editing a link) */}
          {currentUrl && (
            <ModalButton className="delete" onClick={onDelete}>
              Delete
            </ModalButton>
          )}
          <ModalButton className="cancel" onClick={onCancel}>
            Cancel
          </ModalButton>
        </div>
      </ModalContent>
    </ModalOverlay>
  );
};

// Style for LINK entities in the editor
const linkStyle = {
  color: "#3b5998",
  textDecoration: "underline",
};

const customStyleFn = (style: any, block: any) => {
  return style;
};

// Decorator strategy for links
const findLinkEntities = (
  contentBlock: any,
  callback: any,
  contentState: any
) => {
  contentBlock.findEntityRanges((character: any) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
};

const Link = (props: any) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a href={url} style={linkStyle}>
      {props.children}
    </a>
  );
};

const compositeDecorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

type CustomDraftEditorCommand = DraftEditorCommand | "soft-insert";

const blockRenderMap = ImmutableMap({
  unstyled: {
    element: "p",
  },
});

// Include 'paragraph' as a valid block and updated the unstyled element but
// keep support for other draft default block types
const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);

const ToolbarContainer = styled.div<{ left: number; top: number }>`
  position: absolute;
  top: ${(props) => props.top}px;
  left: ${(props) => props.left}px;
  transform: translate(-50%) scale(1);
  border-radius: 2px;
  box-shadow: 0px 1px 3px 0px rgba(220, 220, 220, 1);
  background: black;
  border-bottom-left-radius: 50px;
  border-top-left-radius: 50px;
  padding-left: 12px;
  border-bottom-right-radius: 50px;
  border-top-right-radius: 50px;
  padding-right: 12px;

  z-index: 2;
  &:after,
  &:before {
    top: 100%;
    left: 50%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  &:after {
    border-color: rgba(255, 255, 255, 0);
    border-top-color: black;
    border-width: 4px;
    margin-left: -4px;
  }
  &:before {
    border-color: rgba(221, 221, 221, 0);
    border-top-color: #ddd;
    border-width: 6px;
    margin-left: -6px;
  }
`;

const ToolbarButton = styled.button`
  color: white;
  background: black;
  border: none;
  font-size: 16px;
  padding: 6px;
  &:hover {
    background: #2f2f2f;
  }
`;

interface toolbarTypes {
  label: string;
  blockType?: string;
  inlineStyle?: string;
  icon?: IconDefinition;
  action?: "LINK"; // Added action property
}

const TOOL_BAR_TYPES: toolbarTypes[] = [
  { label: "F", inlineStyle: "BOLD" },
  { label: "K", inlineStyle: "ITALIC" },
  { label: "H1", blockType: "header-one" },
  { label: "H2", blockType: "header-two" },
  { label: "H3", blockType: "header-three" },
  { label: "H4", blockType: "header-four" },
  { label: "UL", blockType: "unordered-list-item", icon: faListUl },
  { label: "OL", blockType: "ordered-list-item", icon: faListOl },
  { label: "Blockquote", blockType: "blockquote", icon: faQuoteRight },
  { label: "Link", action: "LINK", icon: faLink }, // Added Link action
];

const RichText = observer((props: { spec: RichTextComponentSpec }) => {
  if (props.spec.content.blocks[0]?.key === "A") {
    props.spec.content.blocks[0].key = nanoid(5);
  }
  if (props.spec.id === undefined) {
    props.spec.id = nanoid(5);
  }
  const [editorState, setEditorState] = useStateWithDeps(
    EditorState.createWithContent(
      convertFromRaw(props.spec.content),
      compositeDecorator // Apply the composite decorator here
    ),
    [props.spec.id]
  );

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkModalData, setLinkModalData] = useState({
    selectedText: "",
    currentUrl: "",
  });

  const handleKeyBinding = useCallback((e: React.KeyboardEvent) => {
    if (e.shiftKey && e.key === "Enter") {
      return "soft-insert";
    } else if (e.key === "Enter") {
    }
    return getDefaultKeyBinding(e);
  }, []);

  const handleKeyCommand = useCallback(
    (command: CustomDraftEditorCommand, editorState: EditorState) => {
      //let newState;
      if (command === "soft-insert") {
        const newState = RichUtils.insertSoftNewline(editorState);
        if (newState) {
          setEditorState(newState);
          return "handled";
        }
      } else if (command === "split-block") {
        console.log("split block");

        // get current Block
        let content = editorState.getCurrentContent();
        const currentBlock = content.getBlockForKey(
          editorState.getSelection().getStartKey()
        );

        // Note: if the previous block is of header type
        if (currentBlock.getType().includes("header")) {
          console.log("changing format");

          // Add new empty block
          const contentState = Modifier.splitBlock(
            editorState.getCurrentContent(),
            editorState.getSelection()
          );
          const newState = EditorState.push(
            editorState,
            contentState,
            "split-block"
          );

          // change new block to unstyled
          const contentNew = newState.getCurrentContent();
          const contentFinal = Modifier.setBlockType(
            contentNew,
            newState.getSelection(),
            "unstyled"
          );
          const finalState = EditorState.push(
            newState,
            contentFinal,
            "change-block-type"
          );

          setEditorState(finalState);
          return "handled";
        }
      } else {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
          setEditorState(newState);
          return "handled";
        }
      }
      return "not-handled";
    },
    []
  );

  const editor = React.useRef<Editor>(null);

  const handleBlur = useCallback(() => {
    props.spec.content = convertToRaw(editorState.getCurrentContent());
  }, [editorState, props.spec.content]);

  const [toolbarState, setToolbarState] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const selection = editorState.getSelection();
  useEffect(() => {
    setTimeout(() => {
      if (selection.isCollapsed() || !selection.getHasFocus())
        return setToolbarState(null);
      const editorRef = editor.current;
      const editorNode = editorRef?.editorContainer;
      if (!editorNode) return setToolbarState(null);
      const editorRootRect = editorNode.getBoundingClientRect();
      const selectionRect = getVisibleSelectionRect(window);
      if (!selectionRect) return;
      const extraTopOffset = -40;
      setToolbarState({
        top:
          editorRootRect.top +
          window.pageYOffset +
          (selectionRect.top - editorRootRect.top) +
          extraTopOffset,
        left:
          editorRootRect.left +
          (selectionRect.left - editorRootRect.left) +
          selectionRect.width / 2,
      });
    });
  }, [selection]);

  const handleInlineClick = useCallback(
    (inlineType: string) => {
      setEditorState(RichUtils.toggleInlineStyle(editorState, inlineType));
    },
    [editorState, setEditorState]
  );

  const handleBlockClick = useCallback(
    (blockType: string) => {
      setEditorState(RichUtils.toggleBlockType(editorState, blockType));
    },
    [editorState, setEditorState]
  );

  const handleLinkAction = useCallback(() => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    let selectedText = "";
    let existingUrl = "";
    let linkKey = null;
    const blockKey = selection.getStartKey(); // Define blockKey here
    const currentContentBlock = contentState.getBlockForKey(blockKey); // Use currentContentBlock

    if (!selection.isCollapsed()) {
      selectedText = currentContentBlock
        .getText()
        .slice(selection.getStartOffset(), selection.getEndOffset());
      linkKey = currentContentBlock.getEntityAt(selection.getStartOffset());
    } else {
      const offset = selection.getStartOffset();
      linkKey = currentContentBlock.getEntityAt(offset);
      if (!linkKey && offset > 0) {
        linkKey = currentContentBlock.getEntityAt(offset - 1);
      }
    }

    if (linkKey) {
      const linkInstance = contentState.getEntity(linkKey);
      if (linkInstance.getType() === "LINK") {
        existingUrl = linkInstance.getData().url;
        if (selection.isCollapsed()) {
          // Use currentContentBlock which is defined in this scope
          currentContentBlock.findEntityRanges(
            (character: any) => character.getEntity() === linkKey, // Explicitly type character
            (start: number, end: number) => {
              // Explicitly type start and end
              selectedText = currentContentBlock.getText().slice(start, end);
            }
          );
        }
      }
    }

    if (!selectedText && !existingUrl && selection.isCollapsed()) {
      alert(
        "Please select text to create a link, or place the cursor within an existing link to edit it."
      );
      return;
    }

    setLinkModalData({
      selectedText: selectedText || "Link",
      currentUrl: existingUrl,
    });
    setIsLinkModalOpen(true);
  }, [editorState]);

  const saveLink = (newUrl: string) => {
    let currentEditorState = editorState;
    const selection = currentEditorState.getSelection();
    const contentState = currentEditorState.getCurrentContent();
    let newEditorState;

    if (!newUrl || newUrl.trim() === "") {
      newEditorState = RichUtils.toggleLink(
        currentEditorState,
        selection,
        null
      );
    } else {
      const contentStateWithEntity = contentState.createEntity(
        "LINK",
        "MUTABLE",
        { url: newUrl }
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const editorStateWithEntity = EditorState.push(
        currentEditorState,
        contentStateWithEntity,
        "apply-entity"
      );
      newEditorState = RichUtils.toggleLink(
        editorStateWithEntity,
        selection,
        entityKey
      );
    }

    setEditorState(newEditorState);
    // Immediately update props.spec.content after state is set
    props.spec.content = convertToRaw(newEditorState.getCurrentContent());
    setIsLinkModalOpen(false);
  };

  const removeLink = () => {
    const selection = editorState.getSelection();
    const newEditorState = RichUtils.toggleLink(editorState, selection, null);
    setEditorState(newEditorState);
    // Immediately update props.spec.content after state is set
    props.spec.content = convertToRaw(newEditorState.getCurrentContent());
    setIsLinkModalOpen(false);
  };

  const cancelLinkEdit = () => {
    setIsLinkModalOpen(false);
  };

  const BlockStyleControls = () => {
    return (
      <div>
        {TOOL_BAR_TYPES.map((type) => (
          <ToolbarButton
            key={type.label} // Added key for React list rendering
            onMouseDown={(e) => {
              // Added event arg
              e.preventDefault(); // Prevent editor losing focus
              if (type.blockType) {
                handleBlockClick(type.blockType);
              } else if (type.inlineStyle) {
                handleInlineClick(type.inlineStyle);
              } else if (type.action === "LINK") {
                handleLinkAction();
              }
            }}
          >
            {type.icon ? <FontAwesomeIcon icon={type.icon} /> : type.label}
          </ToolbarButton>
        ))}
      </div>
    );
  };

  const { isEditing } = useContext(PageContext);

  const onEsc = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      editor.current?.blur();
    }
  };

  return (
    <div onKeyDown={onEsc}>
      <EditorStyle>
        <Editor
          editorState={editorState}
          handleKeyCommand={handleKeyCommand}
          readOnly={!isEditing}
          onChange={setEditorState}
          onBlur={handleBlur}
          blockRenderMap={extendedBlockRenderMap}
          ref={editor as any} // Ensure ref is cast to any
          keyBindingFn={handleKeyBinding}
        />
      </EditorStyle>
      {toolbarState ? (
        <ToolbarContainer
          top={toolbarState.top}
          left={toolbarState.left}
          onMouseDown={(e) => e.preventDefault()}
        >
          <BlockStyleControls />
        </ToolbarContainer>
      ) : null}
      <LinkEditModal
        isOpen={isLinkModalOpen}
        selectedText={linkModalData.selectedText}
        currentUrl={linkModalData.currentUrl}
        onSave={saveLink}
        onDelete={removeLink}
        onCancel={cancelLinkEdit}
      />
    </div>
  );
});

const RichTextComponentProps = observer(() => {
  return <div>This component has no properties</div>;
});

const TextComponentItem = observer(
  (props: ComponentItemProps<RichTextComponentSpec>) => {
    return <DefaultItem {...props} name="Rich Text" icon={faFont} />;
  }
);

const New = (props: {}) => {
  const baseRawContent: RawDraftContentState = {
    blocks: [
      {
        key: "A",
        text: "Enter text here",
        type: "unstyled",
        depth: 0,
        entityRanges: [],
        inlineStyleRanges: [],
      },
    ],
    entityMap: {},
  };

  const [, drag] = useDrag<DragSpec, unknown, unknown>(() => ({
    type: GENERIC_COMPONENT,
    item: {
      spec: {
        type: RICH_TEXT_COMPONENT,
        content: baseRawContent,
      },
    },
  }));
  return (
    <NewItem
      isActive={false}
      ref={(elem) => {
        drag(elem);
      }}
    >
      <FontAwesomeIcon icon={faFont} />
      <span> Rich Text </span>
    </NewItem>
  );
};

export const RichTextComponent: IEditableComponent<RichTextComponentSpec> = {
  component: RichText,
  properties: RichTextComponentProps,
  item: TextComponentItem,
  new: New,
};
